import React from 'react';
import { teboxDesignSystem, useBookingTebox } from '../../styles/tebox-booking';
import { TeeTimeCard } from './TeeTimeCard';
import { TeeTimeListRow } from './TeeTimeListRow';
import TeeSheetView from './TeeSheetView';
import { Skeleton, Alert, AlertTitle, AlertDescription, useIsMobile } from '../ui';

// Define interface inline to bypass module resolution issues
// Updated: 2025-01-07 - Fixed date handling issues
interface TeeTime {
  id: string;
  courseName: string;
  courseId: string;
  dateTime: Date;
  originalDate?: string; // Original date string from backend (YYYY-MM-DD) - made optional
  price: number;
  capacity: number;
  bookedCount: number;
  availableSpots: number;
  courseColor?: string; // Optional course color for the dot
  customerTypeId?: string; // Add customer type ID for pricing
  pricingOptions?: Array<{
    customerTypeId: string;
    customerTypeName: string;
    price: number;
    color: string;
  }>; // All available pricing options for this tee time
  memberPlayers?: Array<{
    spotNumber: number;
    firstName: string;
    lastName: string;
    customerType: string;
  }>; // Member player information for member users
  dynamicPricing?: {
    direction: 'lower' | 'higher';
    percentRounded: number;
    underOnePercent: boolean;
  };
  promotion?: {
    type: 'percentage' | 'fixed';
    value: number;
    discountGreenFee?: boolean;
    discountCart?: boolean;
  };
  mandatoryHalfCartPreload?: boolean;
  courseDefaultGuestTypeId?: string;
  coursePreloadHalfCartWithGreenFee?: boolean;
  priceIncludesHalfCartAddon?: boolean;
}

interface BrandColors {
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  fontFamily: string;
  buttonColor?: string;
  headingTextBold?: boolean;
  headingTextItalic?: boolean;
}

export interface ResultsGridProps {
  teeTimes: TeeTime[];
  isLoading: boolean;
  error: string | null;
  onBook: (teeTime: TeeTime) => void;
  isBooking: boolean;
  viewMode: 'grid' | 'teesheet';
  onViewModeChange: (mode: 'grid' | 'teesheet') => void;
  isMember?: boolean; // Flag to show member information
  selectedDate?: Date; // Selected date for tee sheet view
  blockedTeeTimes?: Set<string>; // Blocked tee times for tee sheet view
  brandColors?: BrandColors;
  /** Course id → admin "preload half cart with green fee" (per-player price includes cart). */
  preloadHalfCartWithGreenFeeByCourseId?: Record<string, boolean>;
  /**
   * Compact cards vs [Make row layout](https://www.figma.com/make/DaVGnkUWa0xomAMEQQ3TWY/New-Reservation-Card).
   * Default is card grid when `viewMode` is grid; tee sheet always uses `TeeSheetView`.
   * List rows: `?teeLayout=list` (or `bookingLayout` / `resultsLayout`).
   */
  gridPresentation?: 'cards' | 'list';
}

export const ResultsGrid: React.FC<ResultsGridProps> = ({ 
  teeTimes, 
  isLoading, 
  error, 
  onBook, 
  isBooking, 
  viewMode, 
  onViewModeChange,
  isMember = false,
  selectedDate = new Date(),
  blockedTeeTimes = new Set(),
  brandColors,
  preloadHalfCartWithGreenFeeByCourseId,
  gridPresentation = 'cards',
}) => {
  void onViewModeChange;
  const tebox = useBookingTebox();
  const ds = teboxDesignSystem;
  const showListRows = gridPresentation === 'list' && viewMode === 'grid';
  const isMobile = useIsMobile();
  const listShellOverflowClass = isMobile ? 'w-full rounded-[6px]' : 'w-full overflow-x-auto rounded-[6px]';
  const listInnerMinWidth: React.CSSProperties = isMobile
    ? {}
    : { minWidth: '820px' };

  if (error) {
    return (
      <Alert variant="destructive" className="w-full">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    if (showListRows) {
      return (
        <div
          className={listShellOverflowClass}
          style={{
            boxShadow: ds.shadows.sm,
            backgroundColor: tebox.colors.bgPrimary,
          }}
        >
          <div
            style={{
              ...listInnerMinWidth,
              padding: `${ds.spacing.sm} ${ds.spacing.md}`,
              boxSizing: 'border-box',
            }}
          >
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton
                key={index}
                className="h-[48px] w-full rounded-[6px] border-b last:border-b-0"
                style={{ borderColor: tebox.colors.border }}
              />
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 md:gap-4 lg:gap-6">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="min-h-[176px] h-[180px] w-full max-w-[400px]" />
        ))}
      </div>
    );
  }

  if (teeTimes.length === 0) {
    return (
      <Alert variant="info" className="w-full">
        <AlertTitle>No Results</AlertTitle>
        <AlertDescription>
          No tee times available for the selected criteria. Try adjusting your filters or selecting a different date.
        </AlertDescription>
      </Alert>
    );
  }



  return (
    <div>
      {showListRows ? (
        <div
          className={listShellOverflowClass}
          style={{
            boxShadow: ds.shadows.sm,
            backgroundColor: tebox.colors.bgPrimary,
          }}
        >
          <div
            style={{
              ...listInnerMinWidth,
              padding: `${ds.spacing.sm} ${ds.spacing.md}`,
              boxSizing: 'border-box',
            }}
          >
            {teeTimes.map((teeTime, index) => (
              <TeeTimeListRow
                key={`${teeTime.courseId}-${teeTime.id}`}
                teeTime={teeTime}
                onBook={onBook}
                isBooking={isBooking}
                isMember={isMember}
                brandColors={brandColors}
                isLast={index === teeTimes.length - 1}
                preloadHalfCartWithGreenFee={
                  teeTime.priceIncludesHalfCartAddon !== undefined
                    ? !!teeTime.priceIncludesHalfCartAddon
                    : !!(
                        teeTime.mandatoryHalfCartPreload ??
                        teeTime.coursePreloadHalfCartWithGreenFee ??
                        preloadHalfCartWithGreenFeeByCourseId?.[teeTime.courseId]
                      )
                }
              />
            ))}
          </div>
        </div>
      ) : viewMode === 'teesheet' ? (
        <div className="h-full">
          <TeeSheetView
            teeTimes={teeTimes}
            selectedDate={selectedDate}
            onTeeTimeSelect={onBook}
            isMember={isMember}
            blockedTeeTimes={blockedTeeTimes}
            brandColors={brandColors}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 md:gap-4 lg:gap-6">
          {teeTimes.map((teeTime) => (
            <TeeTimeCard
              key={`${teeTime.courseId}-${teeTime.id}`}
              teeTime={teeTime}
              onBook={onBook}
              isBooking={isBooking}
              isMember={isMember}
              brandColors={brandColors}
              preloadHalfCartWithGreenFee={
                teeTime.priceIncludesHalfCartAddon !== undefined
                  ? !!teeTime.priceIncludesHalfCartAddon
                  : !!(
                      teeTime.mandatoryHalfCartPreload ??
                      teeTime.coursePreloadHalfCartWithGreenFee ??
                      preloadHalfCartWithGreenFeeByCourseId?.[teeTime.courseId]
                    )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};
